import { useEffect, useState, type FormEvent, type ReactNode } from "react";

type Api = <T>(
  path: string,
  options?: { method?: string; body?: unknown; csrf?: string | undefined },
) => Promise<T>;
type Settings = {
  enabled: boolean;
  voice: string;
  model: string;
  units: string;
  timezone: string;
  greeting: string;
  closingMorning: string;
  closingAfternoon: string;
  closingEvening: string;
  dateClosings: Record<string, string>;
  location: { name: string; latitude: number; longitude: number };
  sponsor: {
    enabled: boolean;
    name: string;
    text: string;
    start: string;
    end: string;
  };
  alertsEnabled: boolean;
  refreshSeconds: number;
  maxObservationAge: number;
  maxForecastAge: number;
  maxAlertAge: number;
  dailyCharacterLimit: number;
};
type State = {
  config: Settings;
  ready: boolean;
  status: string;
  extension: string;
  lastRefresh: string | null;
  mappedEndpoints: number;
  voices: string[];
  usage: Record<string, number>;
  usageUnit: string;
  weather: {
    observation?: { timestamp: string; stationId: string; stationName: string };
    errors?: string[];
  };
  announcement: { current: string; forecast: string; notice: string };
};
const path = "/admin/time-temperature";
const date = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : "Not yet refreshed";
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="cr-field">
      <span>{label}</span>
      {children}
    </label>
  );
}
export function TimeTemperature({
  api,
  csrf,
}: {
  api: Api;
  csrf?: string | undefined;
}) {
  const [state, setState] = useState<State | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [preview, setPreview] = useState<{
    audioBase64: string;
    text: string;
    generatedAt: string;
  } | null>(null);
  const [overrides, setOverrides] = useState("");
  async function load(reset = false) {
    const result = await api<State>(path);
    setState(result);
    if (reset) {
      setSettings(result.config);
      setOverrides(
        Object.entries(result.config.dateClosings)
          .map(([day, text]) => `${day} | ${text}`)
          .join("\n"),
      );
    }
  }
  useEffect(() => {
    let live = true;
    void api<State>(path)
      .then((result) => {
        if (live) {
          setState(result);
          setSettings(result.config);
          setOverrides(
            Object.entries(result.config.dateClosings)
              .map(([day, text]) => `${day} | ${text}`)
              .join("\n"),
          );
        }
      })
      .catch((cause) => {
        if (live) setError((cause as Error).message);
      });
    return () => {
      live = false;
    };
  }, [api]);
  async function action(name: string, body: unknown = {}) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await api<any>(`${path}/${name}`, {
        method: "POST",
        body,
        csrf,
      });
      if (name === "preview") setPreview(result);
      else {
        setNotice(
          name === "config"
            ? "Settings saved. New recordings are being prepared; calls use the previous complete voice set until ready."
            : name === "sync-locations"
              ? `${result.mapped ?? 0} phone endpoints linked to service addresses. ${result.unresolved?.length ?? 0} addresses need review.`
              : "Weather refresh and missing audio generation queued.",
        );
        await load();
      }
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!settings) return;
    const dateClosings: Record<string, string> = {};
    for (const line of overrides.split("\n").filter((line) => line.trim())) {
      const [day, ...parts] = line.split("|");
      if (
        !day ||
        !/^\d{4}-\d{2}-\d{2}$/.test(day.trim()) ||
        !parts.join("|").trim()
      ) {
        setError("Use YYYY-MM-DD | Closing text for each date override.");
        return;
      }
      dateClosings[day.trim()] = parts.join("|").trim();
    }
    await action("config", { ...settings, dateClosings });
  }
  if (!state || !settings)
    return (
      <section className="cr-panel">
        {error ? (
          <>
            <p role="alert">{error}</p>
            <button
              className="cr-button"
              onClick={() => {
                void load(true).catch((cause) =>
                  setError((cause as Error).message),
                );
              }}
            >
              Retry loading Time &amp; Temperature
            </button>
          </>
        ) : (
          <p role="status">Loading announcement settings and weather status…</p>
        )}
      </section>
    );
  const change = <K extends keyof Settings>(key: K, value: Settings[K]) =>
    setSettings({ ...settings, [key]: value });
  return (
    <div className="cr-time-temperature">
      <p className="cr-page-intro">
        A hometown time and weather line. Dial {state.extension} from a
        connected Cache River phone. No public number is assigned.
      </p>
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="cr-notice" role="status">
          {notice}
        </p>
      )}
      <section className="cr-panel" aria-labelledby="tt-report">
        <h2 id="tt-report">Current announcement</h2>
        <p>
          {state.ready ? state.status : "Preparing the first recordings"}. Last
          refresh: {date(state.lastRefresh)}.
        </p>
        {state.announcement.notice && (
          <p className="cr-notice">{state.announcement.notice}</p>
        )}
        <p>
          {state.announcement.current} {state.announcement.forecast}
        </p>
        <p className="cr-form-note">
          Observation:{" "}
          {state.weather.observation
            ? `${state.weather.observation.stationName} (${state.weather.observation.stationId}), ${date(state.weather.observation.timestamp)}`
            : "No valid observation received yet"}
          .
        </p>
        {!!state.weather.errors?.length && (
          <p className="cr-notice">
            {state.weather.errors.join(". ")}. The caller hears an unavailable
            message when a report exceeds the freshness limit.
          </p>
        )}
        <div className="cr-actions">
          <button
            className="cr-button"
            disabled={busy || !state.ready}
            onClick={() => void action("preview", { kind: "full" })}
          >
            Preview saved announcement
          </button>
          <button
            className="cr-button secondary"
            disabled={busy}
            onClick={() => void action("refresh")}
          >
            Refresh weather / prepare audio
          </button>
          <button
            className="cr-text-button"
            disabled={busy}
            onClick={() => {
              void load().catch((cause) => setError((cause as Error).message));
            }}
          >
            Check status
          </button>
        </div>
        {preview && (
          <div className="cr-tt-preview">
            <p>
              AI-generated announcer. Preview assembled{" "}
              {date(preview.generatedAt)}; calls calculate the time after the
              greeting.
            </p>
            <audio
              controls
              src={`data:audio/wav;base64,${preview.audioBase64}`}
              aria-label="Time and Temperature preview"
              onError={() =>
                setError(
                  "The announcement was generated, but the browser could not play it. Refresh this page and try the preview again.",
                )
              }
            />
            <p>{preview.text}</p>
          </div>
        )}
        <p className="cr-form-note">
          The full announcement plays automatically. Press 1 for an extended
          forecast, 2 to repeat, 0 for time and temperature, or 3 for warning
          details.
        </p>
      </section>
      <section className="cr-panel" aria-labelledby="tt-address">
        <h2 id="tt-address">Customer location</h2>
        <p>
          Weather follows the service address attached to the caller’s
          authenticated phone endpoint, not caller ID. {state.mappedEndpoints}{" "}
          endpoints are linked. Address changes sync every 15 minutes.
        </p>
        <p>
          Phones without a saved connection and service address use the default
          location below. Address matching uses the U.S. Census geocoder; this
          does not replace E911 verification.
        </p>
        <button
          className="cr-button secondary"
          disabled={busy}
          onClick={() => void action("sync-locations")}
        >
          Sync customer service addresses
        </button>
      </section>
      <form className="cr-form cr-panel" onSubmit={save}>
        <fieldset className="cr-form-fields" disabled={busy}>
          <legend>Announcement settings</legend>
          <label className="cr-check">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => change("enabled", e.target.checked)}
            />
            Enable Time &amp; Temperature
          </label>
          <div className="cr-form-grid">
            <Field label="Default location">
              <input
                required
                maxLength={120}
                value={settings.location.name}
                onChange={(e) =>
                  change("location", {
                    ...settings.location,
                    name: e.target.value,
                  })
                }
              />
            </Field>
            <Field label="Timezone">
              <input
                required
                value={settings.timezone}
                onChange={(e) => change("timezone", e.target.value)}
              />
            </Field>
            <Field label="Latitude">
              <input
                required
                type="number"
                min={-90}
                max={90}
                step="0.0001"
                value={settings.location.latitude}
                onChange={(e) =>
                  change("location", {
                    ...settings.location,
                    latitude: Number(e.target.value),
                  })
                }
              />
            </Field>
            <Field label="Longitude">
              <input
                required
                type="number"
                min={-180}
                max={180}
                step="0.0001"
                value={settings.location.longitude}
                onChange={(e) =>
                  change("location", {
                    ...settings.location,
                    longitude: Number(e.target.value),
                  })
                }
              />
            </Field>
            <Field label="Temperature units">
              <select
                value={settings.units}
                onChange={(e) => change("units", e.target.value)}
              >
                <option value="F">Fahrenheit</option>
                <option value="C">Celsius</option>
              </select>
            </Field>
            <Field label="Announcer voice">
              <select
                value={settings.voice}
                onChange={(e) => change("voice", e.target.value)}
              >
                {state.voices.map((voice) => (
                  <option key={voice}>{voice}</option>
                ))}
              </select>
            </Field>
          </div>
          <p className="cr-form-note">
            One OpenAI announcer voice is used for every segment. Save a changed
            voice, wait for audio preparation, then preview it.
          </p>
          <button
            type="button"
            className="cr-button secondary"
            onClick={() => void action("preview", { kind: "voice" })}
          >
            Preview saved voice
          </button>
          <Field label="Greeting">
            <textarea
              required
              maxLength={180}
              value={settings.greeting}
              onChange={(e) => change("greeting", e.target.value)}
            />
          </Field>
          {(
            ["closingMorning", "closingAfternoon", "closingEvening"] as const
          ).map((key, i) => (
            <Field
              key={key}
              label={`${["Morning", "Afternoon", "Evening"][i]} closing`}
            >
              <input
                required
                maxLength={180}
                value={settings[key]}
                onChange={(e) => change(key, e.target.value)}
              />
            </Field>
          ))}
          <Field label="Date-specific closings (optional; one YYYY-MM-DD | Closing text per line)">
            <textarea
              value={overrides}
              onChange={(e) => setOverrides(e.target.value)}
            />
          </Field>
          <h2>Sponsor</h2>
          <label className="cr-check">
            <input
              type="checkbox"
              checked={settings.sponsor.enabled}
              onChange={(e) =>
                change("sponsor", {
                  ...settings.sponsor,
                  enabled: e.target.checked,
                })
              }
            />
            Play the approved sponsor message during its date range
          </label>
          <Field label="Sponsor name">
            <input
              maxLength={80}
              value={settings.sponsor.name}
              onChange={(e) =>
                change("sponsor", { ...settings.sponsor, name: e.target.value })
              }
            />
          </Field>
          <Field label="Exact approved announcement (up to 32 words)">
            <textarea
              maxLength={220}
              value={settings.sponsor.text}
              onChange={(e) =>
                change("sponsor", { ...settings.sponsor, text: e.target.value })
              }
            />
          </Field>
          <div className="cr-form-grid">
            {(["start", "end"] as const).map((key) => (
              <Field
                key={key}
                label={key === "start" ? "Start date" : "End date (inclusive)"}
              >
                <input
                  type="date"
                  value={settings.sponsor[key]}
                  onChange={(e) =>
                    change("sponsor", {
                      ...settings.sponsor,
                      [key]: e.target.value,
                    })
                  }
                />
              </Field>
            ))}
          </div>
          <button
            type="button"
            className="cr-button secondary"
            onClick={() => void action("preview", { kind: "sponsor" })}
          >
            Preview saved sponsor
          </button>
          <h2>Weather and usage limits</h2>
          <label className="cr-check">
            <input
              type="checkbox"
              checked={settings.alertsEnabled}
              onChange={(e) => change("alertsEnabled", e.target.checked)}
            />
            Announce relevant severe weather warnings
          </label>
          <div className="cr-form-grid">
            <Field label="Refresh interval (seconds, 300–3600)">
              <input
                type="number"
                min={300}
                max={3600}
                value={settings.refreshSeconds}
                onChange={(e) =>
                  change("refreshSeconds", Number(e.target.value))
                }
              />
            </Field>
            <Field label="Maximum observation age (seconds, 1800–10800)">
              <input
                type="number"
                min={1800}
                max={10800}
                value={settings.maxObservationAge}
                onChange={(e) =>
                  change("maxObservationAge", Number(e.target.value))
                }
              />
            </Field>
            <Field label="Daily speech character cap">
              <input
                type="number"
                min={1000}
                max={500000}
                value={settings.dailyCharacterLimit}
                onChange={(e) =>
                  change("dailyCharacterLimit", Number(e.target.value))
                }
              />
            </Field>
          </div>
          <p className="cr-form-note">
            Usage:{" "}
            {Object.entries(state.usage)
              .map(([day, count]) => `${day}: ${count.toLocaleString()}`)
              .join("; ") || "No speech requests yet"}
            . {state.usageUnit}.
          </p>
          <button className="cr-button">
            {busy ? "Working…" : "Save announcement settings"}
          </button>
        </fieldset>
      </form>
    </div>
  );
}
